import { ENV } from '@/config/env'
import { logger } from '@/utils/logger'
import type {
  ExchangeFormData,
  TokenValidationResult,
  ProfileAccessResult,
} from '@/types'

const CTX = 'API'

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET'
  const url = `${ENV.API_BASE_URL}${path}`

  logger.info(CTX, `→ ${method} ${url}`)
  if (init?.body) {
    logger.debug(CTX, 'request body', JSON.parse(init.body as string))
  }

  let res: Response
  try {
    res = await fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    })
  } catch (err) {
    logger.error(CTX, `✗ ${method} ${url} — network error`, err)
    throw err
  }

  if (!res.ok) {
    logger.error(CTX, `← ${res.status} ${res.statusText}  ${method} ${url}`)
    throw new ApiError(res.status, `${res.status}`)
  }

  const data = (await res.json()) as T
  logger.info(CTX, `← ${res.status} ${method} ${url}`)
  logger.debug(CTX, 'response body', data)
  return data
}

export async function validateToken(
  token: string,
): Promise<TokenValidationResult> {
  return request<TokenValidationResult>(`/businesscard/${token}`, {
    headers: { Accept: 'application/json' },
  })
}

export async function submitExchange(
  token: string,
  data: ExchangeFormData,
): Promise<{ profileAccessToken: string }> {
  return request<{ profileAccessToken: string }>('/qr/exchange', {
    method: 'POST',
    body: JSON.stringify({ token, ...data }),
  })
}

export async function getProfileAccess(
  accessToken: string,
): Promise<ProfileAccessResult> {
  return request<ProfileAccessResult>(`/profile/${accessToken}`, {
    headers: { Accept: 'application/json' },
  })
}
